/**
 * runLabel: the one name a run reads by, everywhere a run is named.
 *
 * `attempt_no` is the count of every run on the work item, whatever its
 * purpose: the first plan, the revised plan after a person's answers, the
 * build, the review run, a retry. "Attempt 4" therefore means the fourth run,
 * not the fourth try, and a reader took it as three failures (2026-10-06).
 * So a run is named by what it was for, and a repeat by why it repeated,
 * never by the number. The number stays on the URL and nowhere a person reads.
 * A fix round's own count is a different number, one a person does read: it
 * is the round the line's cap counts ("round 2 of 8").
 */
import type { RunReason, WorkItemRunListEntry } from "@yagni/shared";

type Named = Pick<
  WorkItemRunListEntry,
  "id" | "purpose" | "kind" | "attemptNo" | "reason" | "fixRound"
>;

/**
 * The purpose word. A review run is `kind: "review"` on purpose `execution`; a
 * Fletcher run is `kind: "qa"`. A fix round is a build too, but it is named by
 * its round ("Fix round 2"): "Build · again" read as the build starting over.
 */
export function runPurposeWord(
  run: Pick<Named, "purpose" | "kind"> & Partial<Pick<Named, "fixRound">>,
): string {
  if (run.kind === "review") return "Review";
  if (run.kind === "qa") return "QA";
  if (typeof run.fixRound === "number" && run.fixRound > 0) return `Fix round ${run.fixRound}`;
  switch (run.purpose) {
    case "planning":
      return "Plan";
    case "execution":
      return "Build";
    case "scoping":
      return "Reading";
    default:
      return "Run";
  }
}

/** The reason clause, in the person's own frame; null when the record gives none. */
export function runReasonClause(reason: RunReason | null | undefined): string | null {
  if (!reason) return null;
  if (reason.kind === "retry") return "retried after an infrastructure stop";
  switch (reason.from) {
    case "answers":
      return "again, after your answers";
    case "feedback":
      return "again, after your note";
    case "prior_stop":
      return "again, after the build stopped";
    default:
      return "again";
  }
}

/**
 * "Plan", "Plan · again, after your answers", "Build · retried after an
 * infrastructure stop". A repeat the record gives no reason for still says
 * "again" when an earlier run of the same purpose exists in `runs`, so the
 * reader knows it is not the first; `runs` may be in any order.
 */
export function runLabel(run: Named, runs: readonly Named[] = []): string {
  const word = runPurposeWord(run);
  const clause =
    runReasonClause(run.reason) ??
    (runs.some(
      (other) =>
        other.id !== run.id && other.attemptNo < run.attemptNo && runPurposeWord(other) === word,
    )
      ? "again"
      : null);
  return clause ? `${word} · ${clause}` : word;
}
